import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  model,
  output,
} from '@angular/core';

import { Icon } from '../icon/icon';
import { IconName } from '../icon/icon-name';

/**
 * One command in the menu. `label` arrives already translated — the consumer
 * owns i18n (it knows its Transloco scope; the menu doesn't). `disabled` is a
 * resolved boolean for the same reason: the consumer knows the row/context.
 */
export interface ActionMenuItem {
  id: string;
  label: string;
  /** Optional sprite icon rendered before the label. */
  icon?: IconName;
  /**
   * Section heading (already translated, like `label`). Consecutive items with
   * the same value are grouped; each group after the first is preceded by a
   * divider, plus this heading when set. Omit on a trailing group for the
   * "divider only" look (e.g. destructive actions).
   */
  section?: string;
  /** Red styling for destructive commands (e.g. delete). */
  danger?: boolean;
  disabled?: boolean;
}

/** Viewport coordinates (e.g. MouseEvent clientX/Y) to open the popup at. */
export interface ActionMenuAnchor {
  x: number;
  y: number;
}

/**
 * ActionMenu (`app-action-menu`) — a ⋮ trigger button with a popup list of
 * commands. Extracted from the grid's row overflow menu so other surfaces can
 * reuse it.
 *
 * `open` is a model: standalone consumers can just let the trigger toggle it,
 * while a coordinating parent (the grid, which keeps at most one menu open
 * across all rows) can drive it and observe changes.
 *
 * Positioning: by default the popup drops below the ⋮ trigger. With `anchor`
 * set (viewport coordinates, e.g. from a contextmenu event) it renders
 * position:fixed at that point instead — for right-click open — clamped so it
 * doesn't overflow the right viewport edge.
 *
 * Dismissal: the menu closes itself on an outside mousedown. Escape is
 * deliberately left to the consumer — a parent that layers popups (grid:
 * row menu vs column menu vs detail overlay) must decide which one Escape
 * closes first, and competing document-level handlers can't coordinate that.
 */
@Component({
  selector: 'app-action-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon],
  template: `
    <button
      type="button"
      (click)="toggle($event)"
      [title]="triggerTitle()"
      class="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-control bg-transparent text-ink-2 hover:bg-ink-1/10"
    >
      <!-- vertical ⋮ — the sprite's arrow-more is a chevron -->
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="12" cy="5" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="12" cy="19" r="1.7" />
      </svg>
    </button>
    @if (open()) {
      <div
        class="z-20 min-w-[210px] rounded-card border border-hairline bg-card p-2.5 shadow-overlay"
        [class.fixed]="anchor() !== null"
        [class.absolute]="anchor() === null"
        [class.right-0]="anchor() === null"
        [class.top-full]="anchor() === null"
        [class.mt-1]="anchor() === null"
        [style.left.px]="anchorX()"
        [style.top.px]="anchor()?.y"
      >
        @for (section of sections(); track $index) {
          @if (!$first) {
            <div class="my-1 border-t border-hairline"></div>
          }
          @if (section.title) {
            <div class="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              {{ section.title }}
            </div>
          }
          @for (item of section.items; track item.id) {
            <button
              type="button"
              (click)="select(item, $event)"
              [disabled]="item.disabled ?? false"
              class="flex w-full items-center gap-2.5 rounded-control px-3 py-2 text-left text-[13px] hover:bg-sunken disabled:opacity-40"
              [class.text-danger]="item.danger"
              [class.text-ink-1]="!item.danger"
            >
              @if (item.icon) {
                <app-icon [name]="item.icon" [size]="15" class="flex-none" />
              }
              {{ item.label }}
            </button>
          }
        }
      </div>
    }
  `,
  host: {
    class: 'relative inline-flex',
    '(document:mousedown)': 'onDocumentMouseDown($event)',
  },
})
export class ActionMenu {
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly items = input.required<ActionMenuItem[]>();
  /** Tooltip for the ⋮ trigger; translate it at the consumer. */
  readonly triggerTitle = input('');
  /** When set, the popup opens fixed at these viewport coords (right-click). */
  readonly anchor = input<ActionMenuAnchor | null>(null);
  readonly open = model(false);

  /** Emits the selected item's id; the menu closes itself. */
  readonly itemSelect = output<string>();

  /** Anchor x clamped so the panel's min-width stays inside the viewport. */
  protected readonly anchorX = computed(() => {
    const anchor = this.anchor();
    return anchor === null ? undefined : Math.min(anchor.x, window.innerWidth - 220);
  });

  /** Items grouped by consecutive `section` value, in input order. */
  protected readonly sections = computed<{ title: string | null; items: ActionMenuItem[] }[]>(
    () => {
      const groups: { title: string | null; items: ActionMenuItem[] }[] = [];
      for (const item of this.items()) {
        const title = item.section ?? null;
        const last = groups[groups.length - 1];
        if (last && last.title === title) {
          last.items.push(item);
        } else {
          groups.push({ title, items: [item] });
        }
      }
      return groups;
    },
  );

  protected toggle(event: Event): void {
    // Don't let the trigger click reach the surface underneath (e.g. row click).
    event.stopPropagation();
    this.open.update((v) => !v);
  }

  protected select(item: ActionMenuItem, event: Event): void {
    event.stopPropagation();
    this.open.set(false);
    this.itemSelect.emit(item.id);
  }

  protected onDocumentMouseDown(event: MouseEvent): void {
    // Primary button only. A right-mousedown is followed by a contextmenu event
    // that decides what happens next (e.g. the grid re-anchoring this menu);
    // closing here would race that reopen — under coalesced change detection
    // the [open] binding value never changes, so the reopen is never propagated
    // and the menu gets stuck closed.
    if (event.button !== 0) return;
    if (this.open() && !this.hostEl.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }
}
